import React, { useState, useEffect, useRef, Suspense, useCallback } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import BugReportIcon from '@mui/icons-material/BugReport';
import DynamicDrawer from '../../common/Drawer';
import CustomSpeedDial from '../../common/SpeedDial';
import NormalTextField from '../../common/NormalTextField';
import MultipleSelect from '../../common/MultipleSelect';
import DynamicSnackbar from '../../common/Snackbar';
import ExpandableDataGrid from '../../common/ExpandableDataGrid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GppGoodIcon from '@mui/icons-material/GppGood';
import Modal from '../../common/Modal';
import FileUploadControls from '../../common/FileUploadControls';
import LoadingButton from '@mui/lab/LoadingButton';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import auditService from '../../../services/AuditService';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';


const ControlsLibrary = () => {



    const [users, setUsers] = useState([]);
    const [apps, setApps] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [controlsData, setControlsData] = useState([]);
    const [newData, setNewData] = useState([]);
    const [controlsType, setControlsType] = useState([]);
    const [controlsCategory, setControlsCategory] = useState([]);
    const [controlsDomain, setControlsDomain] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const saveTimeout = useRef(null);
    const [controlsExist, setControlsExist] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [openControlsModal, setOpenControlsModal] = useState(false);
    const [controlsUpload, setControlsUpload] = useState([]);
    
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadComplete, setIsUploadComplete] = useState(false);
    
    // Add state variables for editing controls
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingControl, setEditingControl] = useState(null);
    const [editControlsType, setEditControlsType] = useState(null);
    const [editControlsCategory, setEditControlsCategory] = useState(null);
    const [editControlsDomain, setEditControlsDomain] = useState(null);
    const [editErrors, setEditErrors] = useState({
        CONTROL_ID: '',
        CONTROL_TITLE: '',
        CONTROL_DESCRIPTION: ''
    });
    const [editFormValid, setEditFormValid] = useState(false);
    const [editIsLoading, setEditIsLoading] = useState(false);
    const [editDescriptionLoading, setEditDescriptionLoading] = useState(false);
    
    // Add state variables for deleting controls
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [controlToDelete, setControlToDelete] = useState(null);
    const [deleteIsLoading, setDeleteIsLoading] = useState(false);
    const [dependencyInfo, setDependencyInfo] = useState(null); // Store information about dependencies

    const [formValid, setFormValid] = useState(false);
    const [isDescriptionLoading, setIsDescriptionLoading] = useState(false);
    
    // Define errors state
    const [errors, setErrors] = useState({
        CONTROL_ID: '',
        CONTROL_TITLE: '',
        CONTROL_DESCRIPTION: '',
    });
    
    // Add validation on change to update formValid state
    useEffect(() => {
        if (!newData) return;
        
        const validControlId = newData.CONTROL_ID && !errors.CONTROL_ID;
        const validControlTitle = newData.CONTROL_TITLE && !errors.CONTROL_TITLE;
        const validControlDescription = newData.CONTROL_DESCRIPTION && !errors.CONTROL_DESCRIPTION;
        
        setFormValid(validControlId && validControlTitle && validControlDescription);
    }, [newData, errors]);
    
    const validateForm = () => {
        let isValid = true;
        const newErrors = {};

        // Validate Control ID
        if (!newData.CONTROL_ID) {
            isValid = false;
            newErrors.CONTROL_ID = 'This field is required';
        } else if (controlsExist.some(control => control.CONTROL_ID === newData.CONTROL_ID)) {
            isValid = false;
            newErrors.CONTROL_ID = 'Controls ID already exists';
        }

        // Validate Control Title
        if (!newData.CONTROL_TITLE) {
            isValid = false;
            newErrors.CONTROL_TITLE = 'This field is required';
        }

        // Validate Description Title
        if (!newData.CONTROL_DESCRIPTION) {
            isValid = false;
            newErrors.CONTROL_DESCRIPTION = 'This field is required';
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleClick = (event, app) => {
        // Find the full control object from controlsData based on CONTROL_ID and appID (which stores the UUID)
        const controlToEdit = controlsData.find(control => control.id === app.appID);
        if (!controlToEdit) return;
        
        // Set up the editing state
        setEditingControl({...controlToEdit});
        
        // Set up the control type dropdown
        if (controlToEdit.CONTROL_TYPE) {
            const matchedType = controlsTypeOptions.find(opt => opt.value === controlToEdit.CONTROL_TYPE);
            setEditControlsType(matchedType || null);
        } else {
            setEditControlsType(null);
        }
        
        // Set up the control category dropdown
        if (controlToEdit.CONTROL_CATEGORY) {
            const matchedCategory = controlsCategoryOptions.find(opt => opt.value === controlToEdit.CONTROL_CATEGORY);
            setEditControlsCategory(matchedCategory || null);
        } else {
            setEditControlsCategory(null);
        }
        
        // Set up the control domain dropdown
        if (controlToEdit.CONTROL_DOMAIN) {
            const matchedDomain = controlsDomainOptions.find(opt => opt.value === controlToEdit.CONTROL_DOMAIN);
            setEditControlsDomain(matchedDomain || null);
        } else {
            setEditControlsDomain(null);
        }
        
        // Clear any errors and set form as valid initially
        setEditErrors({
            CONTROL_ID: '',
            CONTROL_TITLE: '',
            CONTROL_DESCRIPTION: ''
        });
        setEditFormValid(true);
        
        // Open the edit modal
        setEditModalOpen(true);
    };

    const toggleDrawer = (open) => () => {
        if (open) {
            // Initialize form fields when opening the drawer
            setNewData({});
            setControlsType(null);
            setControlsCategory(null);
            setControlsDomain(null);
            setErrors({
                CONTROL_ID: '',
                CONTROL_TITLE: '',
                CONTROL_DESCRIPTION: ''
            });
            setFormValid(false);
        } else {
            // Clear form fields when closing the drawer
            setNewData({});
            setControlsType(null);
            setControlsCategory(null);
            setControlsDomain(null);
            setErrors({
                CONTROL_ID: '',
                CONTROL_TITLE: '',
                CONTROL_DESCRIPTION: ''
            });
            setFormValid(false);
        }
        setOpen(open);
    };

    const renderLoadingState = () => (
        <mui.Box 
            sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '80vh',
                backgroundColor: 'whitesmoke'
            }}
        >
            <mui.Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={60} thickness={4} />
                <mui.Typography 
                    variant="h6" 
                    sx={{ 
                        mt: 2, 
                        color: 'text.secondary',
                        fontWeight: 500
                    }}
                >
                    Loading Controls Library...
                </mui.Typography>
            </mui.Box>
        </mui.Box>
    );

    useEffect(() => {
        const fetchControls = async () => {
            try {
                setIsLoading(true);
                const controlsList = await auditService.fetchControls();

                if (controlsList) {
                    setControlsData(controlsList);
                    setControlsExist(controlsList);
                }
            } catch (fetchError) {
                console.error('Error fetching apps:', fetchError);
                setControlsData([]);
                setControlsExist([]);
            } finally {
                // Add a minimum delay of 1.5 seconds before hiding the loading state
                setTimeout(() => {
                    setIsLoading(false);
                }, 1000);
            }
        };
        fetchControls();
    }, []);

    useEffect(() => {
        const isExistingControls = controlsExist.some(controls => controls.CONTROL_ID === controlsData.CONTROL_ID);
        if (isExistingControls) {
            setError('Controls ID already exists');
        } else {
            setError('');
        }
    }, [controlsData]);

    const handleOpenControlsModal = () => {
        setOpenControlsModal(true);
    };

    const handleCloseControlsModal = (event, reason) => {
        // Only prevent default closing if reason is backdropClick
        if (reason === 'backdropClick') {
            return;
        }
        // For any other reason, close the modal
        setOpenControlsModal(false);
        
        // Reset upload states when closing the modal
        setControlsUpload([]);
        setIsUploadComplete(false);
    };

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'CONTROL_ID', headerName: 'Control ID', flex: .2 },
        { field: 'CONTROL_TITLE', headerName: 'Control Name', flex: .2 },
        { field: 'CONTROL_TYPE', headerName: 'Control Type', flex: .2 },
        { field: 'CONTROL_DESCRIPTION', headerName: 'Control Description', flex: 1 },
    ];

    // Create the initial rows from controls data
    const initialRows = Array.isArray(controlsData) ? controlsData.map((controls, index) => ({
        id: index + 1,
        CONTROL_ID: controls.CONTROL_ID || '-',
        CONTROL_TITLE: controls.CONTROL_TITLE || '-',
        CONTROL_TYPE: controls.CONTROL_TYPE || '-',
        CONTROL_DESCRIPTION: controls.CONTROL_DESCRIPTION || '-',
        CONTROL_DOMAIN: controls.CONTROL_DOMAIN || '-',
        CONTROL_CATEGORY: controls.CONTROL_CATEGORY || '-',
      
        appID: controls.id,
    })) : [];

    // Filter rows based on search query
    const rows = searchQuery.trim() === '' ? initialRows : initialRows.filter(row => {
        const searchLower = searchQuery.toLowerCase();
        return (
            (row.CONTROL_ID && row.CONTROL_ID.toLowerCase().includes(searchLower)) ||
            (row.CONTROL_TITLE && row.CONTROL_TITLE.toLowerCase().includes(searchLower)) ||
            (row.CONTROL_TYPE && row.CONTROL_TYPE.toLowerCase().includes(searchLower)) ||
            (row.CONTROL_DESCRIPTION && row.CONTROL_DESCRIPTION.toLowerCase().includes(searchLower)) ||
            (row.CONTROL_DOMAIN && row.CONTROL_DOMAIN.toLowerCase().includes(searchLower)) ||
            (row.CONTROL_CATEGORY && row.CONTROL_CATEGORY.toLowerCase().includes(searchLower)) 
        );
    });


    const renderAuditPrepButton = (params) => {
        const app = params.row;

        return (
            <mui.Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Edit Control" arrow>
                    <mui.IconButton
                        size="small"
                        onClick={(event) => handleClick(event, app)}
                    >
                        <EditIcon sx={{ fontSize: '18px' }} />
                    </mui.IconButton>
                </Tooltip>
                <Tooltip title="Delete Control" arrow>
                    <mui.IconButton
                        size="small"
                        color="default"
                        onClick={() => handleDeleteClick(app)}
                        sx={{
                            color: 'rgba(0, 0, 0, 0.54)',
                            '&:hover': {
                                color: 'rgba(211, 47, 47, 0.9)',
                                backgroundColor: 'rgba(211, 47, 47, 0.08)'
                            }
                        }}
                    >
                        <DeleteIcon sx={{fontSize: '18px'}} />
                    </mui.IconButton>
                </Tooltip>
            </mui.Box>
        );
    };

    const columnsWithActions = [
        ...columns,
        {
            field: 'actions',
            headerName: 'Modify',
            width: 200,
            sortable: false,
            renderCell: renderAuditPrepButton,
        },
    ];

    const createControls = async (e) => {
        e.preventDefault();

        // Run final validation to ensure the form is valid
        const isFormValid = validateForm() && formValid;

        if (isFormValid) {
            try {
                setIsLoading(true);
                const newControls = await auditService.createControls(newData);
                if (newControls) {
                    setIsLoading(false);
                    setSnackbarMessage('Successfully created controls');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);

                    setTimeout(() => {
                        window.location.reload(); // Reload the page
                    }, 200);
                }
            } catch (error) {
                setIsLoading(false);
                console.error('Error creating controls record:', error);
                setSnackbarMessage('There was a problem creating controls');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } else {
            setSnackbarMessage('Please fill in all required fields correctly');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const controlsCategoryOptions = [
        { value: 'Business Process', label: 'Business Process' },
        { value: 'IT', label: 'IT' },
        { value: 'Operational', label: 'Operational' },
        { value: 'Entity Level', label: 'Entity Level' },
    ];

    const controlsDomainOptions = [
        { value: 'Access Security', label: 'Access Security' },
        { value: 'Change Management', label: 'Change Management' },
        { value: 'IT Operations', label: 'IT Operations' },
        { value: 'Account Balances', label: 'Account Balances' },
    ];

    const controlsTypeOptions = [
        { value: 'Preventive', label: 'Preventive' },
        { value: 'Detective', label: 'Detective' },
        { value: 'Monitoring', label: 'Monitoring' },
    ];

    const handleControlsTypeChange = (selectedType) => {
        const controlstype = selectedType.value
        setControlsType(selectedType)

        const updatedData = {
            ...newData,
            CONTROL_TYPE: controlstype
        };

        setNewData(updatedData);

    };

    const handleControlsCategoryChange = (selectedType) => {
        const controlscategory = selectedType.value
        setControlsCategory(selectedType)

        const updatedData = {
            ...newData,
            CONTROL_CATEGORY: controlscategory
        };

        setNewData(updatedData);

    };


    const handleControlsDomainChange = (selectedType) => {
        const controlsdomain = selectedType.value
        setControlsDomain(selectedType)

        const updatedData = {
            ...newData,
            CONTROL_DOMAIN: controlsdomain
        };

        setNewData(updatedData);
    };

    // Generate control description using AI
    const handleGenerateDescription = async () => {
        // Check if we have at least a control title
        if (!newData.CONTROL_TITLE) {
            setSnackbarMessage('Please enter a Control Name before generating a description');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        setIsDescriptionLoading(true);
        try {
            const response = await auditService.generateControlDescription({
                control_title: newData.CONTROL_TITLE,
                control_type: newData.CONTROL_TYPE || '',
                control_category: newData.CONTROL_CATEGORY || '',
                control_domain: newData.CONTROL_DOMAIN || ''
            });

            // Update the control description field with the generated description
            const updatedData = {
                ...newData,
                CONTROL_DESCRIPTION: response.description
            };
            setNewData(updatedData);

            // Clear any error for the description field
            setErrors({
                ...errors,
                CONTROL_DESCRIPTION: ''
            });

            setSnackbarMessage('Control description generated successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error generating control description:', error);
            setSnackbarMessage('Failed to generate control description');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsDescriptionLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        const updatedData = {
            ...newData,
            [name]: value
        };

        setNewData(updatedData);
        
        // Validate as the user types
        const newErrors = { ...errors };
        
        switch (name) {
            case 'CONTROL_ID':
                if (!value.trim()) {
                    newErrors[name] = 'This field is required';
                } else if (controlsExist.some(control => control.CONTROL_ID === value)) {
                    newErrors[name] = 'Controls ID already exists';
                } else {
                    newErrors[name] = '';
                }
                break;
            case 'CONTROL_TITLE':
                newErrors[name] = !value.trim() ? 'This field is required' : '';
                break;
            case 'CONTROL_DESCRIPTION':
                newErrors[name] = !value.trim() ? 'This field is required' : '';
                break;
            default:
                break;
        }
        
        setErrors(newErrors);
    };

    const handleControlBlur = (e) => {
        const { name, value } = e.target;

        const updatedData = {
            ...newData,
            [name]: value
        };

        setNewData(updatedData);

        // Full validation on blur
        const newErrors = { ...errors };

        switch (name) {
            case 'CONTROL_ID':
                if (!value.trim()) {
                    newErrors[name] = 'This field is required';
                } else if (controlsExist.some(control => control.CONTROL_ID === value)) {
                    newErrors[name] = 'Controls ID already exists';
                } else {
                    newErrors[name] = '';
                }
                break;
            case 'CONTROL_TITLE':
                newErrors[name] = !value.trim() ? 'This field is required' : '';
                break;
            case 'CONTROL_DESCRIPTION':
                newErrors[name] = !value.trim() ? 'This field is required' : '';
                break;
            default:
                break;
        }
        
        setErrors(newErrors);
    };

    const handleButtonClick = async () => {
        if (!controlsUpload || controlsUpload.length === 0) {
            setSnackbarMessage('No data to upload');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        setIsLoading(true);
        setIsUploadComplete(false);

        try {
            console.log('Uploading controls data:', controlsUpload);
            const control_library = await auditService.createControls(controlsUpload);
            
            if (control_library) {
                console.log('Upload successful, received data:', control_library);
                setControlsData(control_library);
                
                setIsLoading(false);  
                setIsUploadComplete(true);
                
                setSnackbarMessage('Successfully uploaded controls');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);

                // Show success state briefly, then refresh the page
                setTimeout(() => {
                    window.location.reload(); // Refresh the page
                }, 1500);
            } else {
                throw new Error('No data returned from server');
            }
        } catch (error) {
            console.error('Error uploading controls:', error);
            setIsLoading(false);
            setIsUploadComplete(false);
            
            setSnackbarMessage('Error uploading controls: ' + (error.message || 'Unknown error'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleOnControlsData = (data) => {
        console.log('Received data from FileUploadControls:', data);
        if (Array.isArray(data) && data.length > 0) {
            // Check for duplicate CONTROL_IDs against existing controls in the database
            const duplicateControls = data.filter(newControl => 
                controlsExist.some(existingControl => 
                    existingControl.CONTROL_ID === newControl.CONTROL_ID
                )
            );
            
            if (duplicateControls.length > 0) {
                const duplicateIds = duplicateControls.map(control => control.CONTROL_ID);
                setSnackbarMessage(`Duplicate control IDs found: ${duplicateIds.join(', ')}. These controls already exist in the system.`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
            
            setControlsUpload(data);
            setIsUploadComplete(false);
            
            // Show notification when data is loaded
            setSnackbarMessage(`Loaded ${data.length} controls ready to upload`);
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
        } else {
            setControlsUpload([]);
            console.warn('Received empty or invalid data from file upload');
        }
    };

    const handleDeleteClick = (app) => {
        // Reset any previous dependency information
        setDependencyInfo(null);
        
        // Find the full control object from controlsData based on appID
        const controlToDelete = controlsData.find(control => control.id === app.appID);
        if (!controlToDelete) return;
        
        // Set the control to delete and open confirmation dialog
        setControlToDelete(controlToDelete);
        setDeleteConfirmOpen(true);
    };
    
    const handleDeleteConfirmClose = () => {
        setDeleteConfirmOpen(false);
        setControlToDelete(null);
    };
    
    const handleDeleteConfirm = async () => {
        if (!controlToDelete || !controlToDelete.id) {
            setSnackbarMessage('Cannot delete: Missing control ID');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleDeleteConfirmClose();
            return;
        }
        
        try {
            setDeleteIsLoading(true);
            
            // Call the deleteControls API with the control UUID
            await auditService.deleteControls(controlToDelete.id);
            
            // Refresh the controls data after deletion
            const updatedControlsList = await auditService.fetchControls();
            if (updatedControlsList) {
                setControlsData(updatedControlsList);
                setControlsExist(updatedControlsList);
            }
            
            setDeleteIsLoading(false);
            handleDeleteConfirmClose();
            
            setSnackbarMessage('Successfully deleted control');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            setDeleteIsLoading(false);
            console.error('Error deleting control record:', error);
            
            // Check for foreign key constraint error
            const errorMessage = error.response?.data?.detail || error.message || '';
            const errorStatus = error.response?.status;
            
            // Handle foreign key constraint violations (often returned as 500 errors)
            if (
                errorStatus === 500 || 
                errorStatus === 409 || 
                errorMessage.includes('foreign key constraint fails') || 
                errorMessage.includes('IntegrityError') ||
                errorMessage.includes('Cannot delete or update a parent row')
            ) {
                // Try to extract table name from the error message for better guidance
                let affectedTable = '';
                if (errorMessage.includes('CONSTRAINT `')) {
                    const constraintMatch = errorMessage.match(/CONSTRAINT `([^`]+)`/);
                    if (constraintMatch && constraintMatch[1]) {
                        affectedTable = constraintMatch[1].split('_')[0]; // Get the first part of the constraint name
                    }
                }
                
                if (errorMessage.includes('auditfile')) {
                    affectedTable = 'Audit Files';
                } else if (errorMessage.includes('AUDITFILE')) {
                    affectedTable = 'Audit Files';
                }
                
                // Set dependency info for UI display
                setDependencyInfo({
                    controlId: controlToDelete?.CONTROL_ID || '',
                    controlName: controlToDelete?.CONTROL_TITLE || '',
                    affectedTable: affectedTable || 'audits'
                });
                
                setSnackbarMessage(
                    'This control cannot be deleted because it is being used in one or more audits. ' +
                    'Please remove all references to this control before deleting it.'
                );
            } else {
                setDependencyInfo(null);
                setSnackbarMessage('There was a problem deleting control: ' + errorMessage);
            }
            
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleDeleteConfirmClose();
        }
    };

    const handleEditModalClose = () => {
        setEditModalOpen(false);
        setEditingControl(null);
        setEditControlsType(null);
        setEditControlsCategory(null);
        setEditControlsDomain(null);
        setEditErrors({
            CONTROL_ID: '',
            CONTROL_TITLE: '',
            CONTROL_DESCRIPTION: ''
        });
    };
    
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        
        const updatedControl = {
            ...editingControl,
            [name]: value
        };
        
        setEditingControl(updatedControl);
        
        // Validate field as user types
        validateEditField(name, value);
    };
    
    const validateEditField = (name, value) => {
        let errorMessage = '';
        
        switch (name) {
            case 'CONTROL_ID':
                if (!value.trim()) {
                    errorMessage = 'Control ID is required';
                } else if (controlsExist.some(control => control.CONTROL_ID === value && control.id !== editingControl.id)) {
                    errorMessage = 'Control ID already exists';
                }
                break;
            case 'CONTROL_TITLE':
                if (!value.trim()) {
                    errorMessage = 'Control Name is required';
                }
                break;
            case 'CONTROL_DESCRIPTION':
                if (!value.trim()) {
                    errorMessage = 'Control Description is required';
                }
                break;
            default:
                break;
        }
        
        setEditErrors(prev => ({
            ...prev,
            [name]: errorMessage
        }));
    };
    
    const handleEditControlsTypeChange = (selectedType) => {
        if (!selectedType) {
            setEditErrors(prev => ({
                ...prev,
                CONTROL_TYPE: 'Control Type is required'
            }));
            return;
        }
        
        const controlType = selectedType.value;
        setEditControlsType(selectedType);
        
        const updatedControl = {
            ...editingControl,
            CONTROL_TYPE: controlType
        };
        
        setEditingControl(updatedControl);
        
        setEditErrors(prev => ({
            ...prev,
            CONTROL_TYPE: ''
        }));
    };
    
    const handleEditControlsCategoryChange = (selectedCategory) => {
        if (!selectedCategory) {
            return;
        }
        
        const controlCategory = selectedCategory.value;
        setEditControlsCategory(selectedCategory);
        
        const updatedControl = {
            ...editingControl,
            CONTROL_CATEGORY: controlCategory
        };
        
        setEditingControl(updatedControl);
    };
    
    const handleEditControlsDomainChange = (selectedDomain) => {
        if (!selectedDomain) {
            return;
        }
        
        const controlDomain = selectedDomain.value;
        setEditControlsDomain(selectedDomain);
        
        const updatedControl = {
            ...editingControl,
            CONTROL_DOMAIN: controlDomain
        };
        
        setEditingControl(updatedControl);
    };
    
    // Generate control description using AI for edit form
    const handleEditGenerateDescription = async () => {
        // Check if we have at least a control title
        if (!editingControl.CONTROL_TITLE) {
            setSnackbarMessage('Please enter a Control Name before generating a description');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        setEditDescriptionLoading(true);
        try {
            const response = await auditService.generateControlDescription({
                control_title: editingControl.CONTROL_TITLE,
                control_type: editingControl.CONTROL_TYPE || '',
                control_category: editingControl.CONTROL_CATEGORY || '',
                control_domain: editingControl.CONTROL_DOMAIN || ''
            });

            // Update the control description field with the generated description
            const updatedControl = {
                ...editingControl,
                CONTROL_DESCRIPTION: response.description
            };
            setEditingControl(updatedControl);

            // Clear any error for the description field
            setEditErrors({
                ...editErrors,
                CONTROL_DESCRIPTION: ''
            });

            setSnackbarMessage('Control description generated successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error generating control description:', error);
            setSnackbarMessage('Failed to generate control description');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setEditDescriptionLoading(false);
        }
    };
    
    // Update the control
    const updateControl = async () => {
        if (!editFormValid || !editingControl) {
            setSnackbarMessage('Please fill all required fields correctly');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        try {
            setEditIsLoading(true);
            
            // Clone the control data to avoid modifying the state directly
            const controlData = { ...editingControl };
        
            // Use the control's id field for the update
            const controlId = controlData.id || '';
            const updatedControl = await auditService.updateControls(controlId, controlData);
            handleSuccessfulUpdate(updatedControl);
            
        } catch (error) {
            setEditIsLoading(false);
            console.error('Error updating control record:', error);
            
            setSnackbarMessage('There was a problem updating control: ' + (error.response?.data?.detail || error.message));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };
    
    // Helper function to handle a successful update
    const handleSuccessfulUpdate = async (updatedControl) => {
        try {
            // Refresh the control data
            const updatedControlsList = await auditService.fetchControls();
            if (updatedControlsList) {
                console.log('Updated controls list:', updatedControlsList);
                setControlsData(updatedControlsList);
                setControlsExist(updatedControlsList);
            }
            
            // Close the modal and clear editing state
            setEditIsLoading(false);
            handleEditModalClose();
            
            // Show success message
            setSnackbarMessage('Successfully updated control');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error refreshing controls after update:', error);
            // Even if refresh fails, still close the modal
            setEditIsLoading(false);
            handleEditModalClose();
            
            // We can still show a success message for the update since it succeeded
            setSnackbarMessage('Control updated, but there was an issue refreshing the list');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
        }
    };
    
    // UseEffect to validate the edit form
    useEffect(() => {
        if (!editingControl) return;
        
        const validControlId = editingControl.CONTROL_ID && !editErrors.CONTROL_ID;
        const validControlTitle = editingControl.CONTROL_TITLE && !editErrors.CONTROL_TITLE;
        const validControlDescription = editingControl.CONTROL_DESCRIPTION && !editErrors.CONTROL_DESCRIPTION;
        
        setEditFormValid(validControlId && validControlTitle && validControlDescription);
    }, [editingControl, editErrors]);

    // Function to download the Controls Library as CSV
    const downloadControlsLibrary = () => {
        if (!initialRows || initialRows.length === 0) {
            setSnackbarMessage('No controls data available to download');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        try {
            // Define the CSV headers based on the columns
            const headers = [
                'Control ID',
                'Control Title',
                'Control Type',
                'Control Description',
                'Control Domain',
                'Control Category'
            ].join(',');

            // Map the rows to CSV format
            const csvRows = initialRows.map(row => {
                // Properly escape fields that might contain commas, quotes, or newlines
                const escapeCsvField = (field) => {
                    if (field === null || field === undefined || field === '-') return '';
                    const stringField = String(field);
                    // If the field contains quotes, commas, or newlines, enclose in quotes and escape quotes
                    if (stringField.includes('"') || stringField.includes(',') || stringField.includes('\n')) {
                        return `"${stringField.replace(/"/g, '""')}"`;
                    }
                    return stringField;
                };

                return [
                    escapeCsvField(row.CONTROL_ID),
                    escapeCsvField(row.CONTROL_TITLE),
                    escapeCsvField(row.CONTROL_TYPE),
                    escapeCsvField(row.CONTROL_DESCRIPTION),
                    escapeCsvField(row.CONTROL_DOMAIN),
                    escapeCsvField(row.CONTROL_CATEGORY)
                ].join(',');
            });

            // Combine headers and rows
            const csvContent = [headers, ...csvRows].join('\n');
            
            // Create a blob from the CSV content
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            
            // Create a temporary link to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Controls_Library_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            // Show success message
            setSnackbarMessage('Controls Library downloaded successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error downloading Controls Library:', error);
            setSnackbarMessage('Failed to download Controls Library');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Function to download CSV template for bulk upload
    const downloadTemplate = () => {
        // Create template CSV content - headers match exactly with the CONTROLSLIST model fields
        const csvContent = [
            "CONTROL_ID,CONTROL_TITLE,CONTROL_TYPE,CONTROL_DESCRIPTION,CONTROL_DOMAIN,CONTROL_CATEGORY",
            "CTRL001,Access Security Control,Preventive,Control to prevent unauthorized access to systems,Access Security,IT",
            "CTRL002,Change Management Control,Detective,Control to detect unauthorized changes to systems,Change Management,IT",
            "CTRL003,Business Process Control,Monitoring,Control to monitor critical business processes,Account Balances,Business Process"
        ].join("\n");
        
        // Create a blob from the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        // Create a temporary link to trigger download
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ControlsLibrary_Template.csv");
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs 
                        aria-label="breadcrumb"
                        sx={{
                            '& .MuiLink-root': {
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }
                        }}
                    >
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">Controls Library</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <GppGoodIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                Controls Library
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Manage and document controls that are relevant to the organization. Track and assess control effectiveness across your audit activities.
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    {isLoading ? (
                        <mui.Box 
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                height: '60vh',
                                backgroundColor: '#f8fafc',
                                borderRadius: 3,
                                boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                            }}
                        >
                            <mui.Box sx={{ textAlign: 'center' }}>
                                <CircularProgress 
                                    size={40} 
                                    thickness={4}
                                    sx={{
                                        color: 'primary.main',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                    }}
                                />
                                <mui.Typography 
                                    variant="body1" 
                                    sx={{ 
                                        mt: 2, 
                                        color: 'text.secondary',
                                        fontWeight: 500
                                    }}
                                >
                                    Loading Controls Library...
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                    ) : (
                        <mui.Box sx={{ 
                            backgroundColor: '#f8fafc', 
                            borderRadius: 3,
                            minHeight: '80vh',
                            boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                        }}>
                            <mui.Paper 
                                elevation={0}
                                sx={{ 
                                    p: 3,
                                    height: '800px',
                                    backgroundColor: 'transparent'
                                }}
                            >
                                {/* Enhanced Search Component */}
                                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <mui.Paper
                                        elevation={0}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 0.5,
                                            pl: 2,
                                            borderRadius: '12px',
                                            backgroundColor: '#fff',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                            transition: 'all 0.3s ease',
                                            border: '1px solid #f0f0f0',
                                            '&:hover': {
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                                borderColor: 'rgba(25, 118, 210, 0.2)'
                                            },
                                            maxWidth: '380px',
                                            position: 'relative'
                                        }}
                                    >
                                        <mui.Box 
                                            component="span" 
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: theme => searchQuery ? theme.palette.primary.main : 'text.secondary',
                                                transition: 'color 0.2s ease'
                                            }}
                                        >
                                            <i className="material-icons" style={{ fontSize: '20px' }}>search</i>
                                        </mui.Box>
                                        <mui.InputBase
                                            placeholder="Search controls..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            sx={{
                                                ml: 1.5,
                                                flex: 1,
                                                fontSize: '0.95rem',
                                                py: 1,
                                                '& .MuiInputBase-input': {
                                                    transition: 'all 0.2s ease',
                                                    '&::placeholder': {
                                                        opacity: 0.7,
                                                        transition: 'opacity 0.2s ease'
                                                    },
                                                    '&:focus::placeholder': {
                                                        opacity: 0.5
                                                    }
                                                }
                                            }}
                                        />
                                        {searchQuery && (
                                            <mui.Fade in={!!searchQuery}>
                                                <mui.IconButton
                                                    size="small"
                                                    aria-label="clear search"
                                                    onClick={() => setSearchQuery('')}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        mr: 0.5,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            color: 'primary.main',
                                                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                                        },
                                                        opacity: 0.7
                                                    }}
                                                >
                                                    <i className="material-icons" style={{ fontSize: '18px' }}>close</i>
                                                </mui.IconButton>
                                            </mui.Fade>
                                        )}
                                        {rows.length !== initialRows.length && (
                                            <mui.Chip
                                                label={`${rows.length} of ${initialRows.length}`}
                                                size="small"
                                                sx={{
                                                    height: '22px',
                                                    ml: 1,
                                                    mr: 0.5,
                                                    fontSize: '0.7rem',
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                    fontWeight: 500,
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        )}
                                    </mui.Paper>
                                    
                                    <mui.Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={downloadControlsLibrary}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontSize: '0.875rem',
                                            borderColor: 'rgba(25, 118, 210, 0.12)',
                                            color: 'primary.main',
                                            px: 2,
                                            py: 0.75,
                                            fontWeight: 500,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                borderColor: 'primary.main',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                            }
                                        }}
                                    >
                                        Download Library
                                    </mui.Button>
                                </mui.Box>
                                
                                <ExpandableDataGrid
                                    rows={rows}
                                    columns={columns}
                                    columnsWithActions={columnsWithActions}
                                    height={700}
                                    expandableFields={['CONTROL_DESCRIPTION']}
                                    sx={{
                                        '& .MuiDataGrid-root': {
                                            border: 'none',
                                            backgroundColor: 'white',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: 'rgba(25, 118, 210, 0.08)',
                                            '&:hover': {
                                                borderColor: 'rgba(25, 118, 210, 0.12)'
                                            }
                                        }
                                    }}
                                />
                            </mui.Paper>
                        </mui.Box>
                    )}

                    <DynamicDrawer
                        open={open} 
                        onClose={toggleDrawer(false)}
                    >
                        <mui.Box sx={{ 
                            padding: '24px',
                            width: '400px',
                            maxWidth: '100%'
                        }}>
                            <mui.Typography variant="h6" sx={{ 
                                mb: 3,
                                fontWeight: 600, 
                                color: 'text.primary',
                                position: 'relative',
                                '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -8,
                                    left: 0,
                                    width: '40px',
                                    height: '3px',
                                    backgroundColor: 'primary.main'
                                }
                            }}>
                                Create New Control
                            </mui.Typography>

                            <mui.Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Control ID <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                    </mui.Typography>
                                    <NormalTextField
                                        error={!!errors.CONTROL_ID}
                                        name="CONTROL_ID"
                                        placeholder="Enter control identifier"
                                        value={newData.CONTROL_ID || ''}
                                        onChange={handleChange}
                                        onBlur={handleControlBlur}
                                        helperText={errors.CONTROL_ID || ' '}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            mt: 0,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px',
                                                height: '40px',
                                            }
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Control Name <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                    </mui.Typography>
                                    <NormalTextField
                                        error={!!errors.CONTROL_TITLE}
                                        name="CONTROL_TITLE"
                                        placeholder="Enter descriptive name"
                                        value={newData.CONTROL_TITLE || ''}
                                        onChange={handleChange}
                                        onBlur={handleControlBlur}
                                        helperText={errors.CONTROL_TITLE || ' '}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            mt: 0,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px',
                                                height: '40px',
                                            }
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Control Category
                                    </mui.Typography>
                                    <MultipleSelect
                                        isMultiSelect={false}
                                        defaultValue={controlsCategory ? controlsCategory : ''}
                                        placeholderText="Select category"
                                        selectOptions={controlsCategoryOptions}
                                        selectedOptions={controlsCategory ? controlsCategory : ''}
                                        handleChange={handleControlsCategoryChange}
                                        customStyles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                                height: '40px',
                                                fontSize: '0.875rem'
                                            })
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Control Description <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                    </mui.Typography>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                        <mui.Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleGenerateDescription}
                                            disabled={isDescriptionLoading || !newData.CONTROL_TITLE}
                                            startIcon={isDescriptionLoading ? <CircularProgress size={16} color="inherit" /> : <i className="material-icons" style={{ fontSize: '16px' }}>auto_awesome</i>}
                                            sx={{
                                                py: 0.5,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                borderColor: 'rgba(25, 118, 210, 0.08)',
                                                color: 'primary.main',
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                '&:hover': {
                                                    borderColor: 'rgba(25, 118, 210, 0.12)',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                                }
                                            }}
                                        >
                                            {isDescriptionLoading ? 'Generating...' : 'Generate with AI'}
                                        </mui.Button>
                                    </mui.Box>
                                    <NormalTextField
                                        error={!!errors.CONTROL_DESCRIPTION}
                                        name="CONTROL_DESCRIPTION"
                                        placeholder="Enter detailed description of the control"
                                        value={newData.CONTROL_DESCRIPTION || ''}
                                        onChange={handleChange}
                                        onBlur={handleControlBlur}
                                        isMultiLine={true}
                                        rows="4"
                                        helperText={errors.CONTROL_DESCRIPTION || ' '}
                                        fullWidth
                                        sx={{
                                            mt: 0,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px',
                                            }
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Control Domain
                                    </mui.Typography>
                                    <MultipleSelect
                                        isMultiSelect={false}
                                        defaultValue={controlsDomain ? controlsDomain : ''}
                                        placeholderText="Select domain"
                                        selectOptions={controlsDomainOptions}
                                        selectedOptions={controlsDomain ? controlsDomain : ''}
                                        handleChange={handleControlsDomainChange}
                                        customStyles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                                height: '40px',
                                                fontSize: '0.875rem'
                                            })
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Control Type
                                    </mui.Typography>
                                    <MultipleSelect
                                        isMultiSelect={false}
                                        defaultValue={controlsType ? controlsType : ''}
                                        placeholderText="Select type"
                                        selectOptions={controlsTypeOptions}
                                        selectedOptions={controlsType ? controlsType : ''}
                                        handleChange={handleControlsTypeChange}
                                        customStyles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                                height: '40px',
                                                fontSize: '0.875rem'
                                            })
                                        }}
                                    />
                                </mui.Box>

                                <mui.Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                                    <mui.Button 
                                        variant="outlined" 
                                        onClick={handleOpenControlsModal}
                                        fullWidth
                                        startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                                        sx={{
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderColor: 'rgba(25, 118, 210, 0.08)',
                                            color: 'text.primary',
                                            '&:hover': {
                                                borderColor: 'rgba(25, 118, 210, 0.12)',
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                            }
                                        }}
                                    >
                                        Bulk Upload
                                    </mui.Button>
                                    <mui.Button 
                                        variant="contained" 
                                        onClick={createControls}
                                        fullWidth
                                        disabled={!formValid || isLoading}
                                        startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                                        sx={{
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            boxShadow: 'none',
                                            backgroundColor: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                            },
                                            '&:disabled': {
                                                backgroundColor: isLoading ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                                                color: isLoading ? 'white' : 'rgba(0, 0, 0, 0.26)',
                                                opacity: isLoading ? 0.7 : 1
                                            }
                                        }}
                                    >
                                        {isLoading ? 'Creating...' : 'Create Control'}
                                    </mui.Button>
                                </mui.Stack>
                            </mui.Box>
                        </mui.Box>
                    </DynamicDrawer>

                    <CustomSpeedDial 
                        actions={[
                            { 
                                name: 'Create Control', 
                                icon: <GppGoodIcon />, 
                                onClick: () => toggleDrawer(true)() 
                            }
                        ]} 
                    />

                    <DynamicSnackbar
                        open={snackbarOpen}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                        onClose={handleSnackbarClose}
                    />

                    <Modal
                        open={openControlsModal}
                        size={'lg'}
                        onClose={handleCloseControlsModal}
                        body={
                            <div>
                                <mui.Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <mui.Typography variant="h6" sx={{ 
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        position: 'relative',
                                        '&:after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -8,
                                            left: 0,
                                            width: '40px',
                                            height: '3px',
                                            backgroundColor: 'primary.main'
                                        }
                                    }}>
                                        Bulk Upload Controls Library
                                    </mui.Typography>
                                    <mui.Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={downloadTemplate}
                                        sx={{
                                            borderRadius: '4px',
                                            textTransform: 'none',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        Download Template
                                    </mui.Button>
                                </mui.Box>
                                <mui.Box sx={{ 
                                    mb: 2, 
                                    p: 1.5, 
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    borderLeft: '3px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: '2px'
                                }}>
                                    <mui.Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Upload a CSV file with control data. The file should contain these required columns: CONTROL_ID, CONTROL_TITLE, CONTROL_TYPE, CONTROL_DESCRIPTION, and CONTROL_DOMAIN.
                                        <br /><br />
                                        <strong>Note:</strong> CONTROL_CATEGORY is optional but recommended for better organization and filtering.
                                    </mui.Typography>
                                </mui.Box>
                                
                                {isUploadComplete ? (
                                    <mui.Box sx={{ 
                                        textAlign: 'center', 
                                        p: 3, 
                                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                        borderRadius: '4px',
                                        mb: 2
                                    }}>
                                        <CloudDoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                                        <mui.Typography variant="h6" color="success.main">
                                            Upload Complete!
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Your controls have been successfully uploaded.
                                        </mui.Typography>
                                    </mui.Box>
                                ) : isLoading ? (
                                    <mui.Box sx={{ 
                                        textAlign: 'center', 
                                        p: 3, 
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        borderRadius: '4px',
                                        mb: 2
                                    }}>
                                        <CircularProgress size={48} sx={{ mb: 1 }} />
                                        <mui.Typography variant="h6" color="primary">
                                            Uploading...
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Please wait while your controls are being processed.
                                        </mui.Typography>
                                    </mui.Box>
                                ) : null}
                                
                                <FileUploadControls onControlsDataChange={handleOnControlsData} />
                                
                                {controlsUpload && controlsUpload.length > 0 && !isLoading && !isUploadComplete && (
                                    <mui.Box sx={{ 
                                        textAlign: 'center', 
                                        p: 2, 
                                        mt: 2,
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        borderRadius: '4px' 
                                    }}>
                                        <mui.Typography variant="body1" color="primary">
                                            {controlsUpload.length} controls ready to upload
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Click the Upload button to proceed
                                        </mui.Typography>
                                    </mui.Box>
                                )}
                            </div>
                        }
                        footer={
                            <mui.Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', p: 2 }}>
                                <mui.Button 
                                    onClick={handleCloseControlsModal}
                                    variant="outlined"
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        borderRadius: '4px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        borderColor: 'rgba(25, 118, 210, 0.08)',
                                        color: 'text.primary',
                                        '&:hover': {
                                            borderColor: 'rgba(25, 118, 210, 0.12)',
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                        }
                                    }}
                                >
                                    {isUploadComplete ? 'Close' : 'Cancel'}
                                </mui.Button>
                                <LoadingButton
                                    onClick={handleButtonClick}
                                    loading={isLoading}
                                    loadingPosition="start"
                                    startIcon={isUploadComplete ? <CloudDoneIcon /> : <CloudUploadIcon />}
                                    variant="contained"
                                    color="primary"
                                    disabled={(!controlsUpload || controlsUpload.length === 0) || isUploadComplete}
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        borderRadius: '4px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        boxShadow: 'none',
                                        backgroundColor: 'primary.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                        },
                                        '&.Mui-disabled': { 
                                            color: isUploadComplete ? 'white' : 'rgba(255, 255, 255, 0.7)', 
                                            backgroundColor: isUploadComplete ? 'success.main' : 'primary.main',
                                            opacity: isUploadComplete ? 1 : 0.7
                                        }
                                    }}
                                >
                                    {isLoading ? 'Uploading...' : isUploadComplete ? 'Upload Complete' : 'Upload'}
                                </LoadingButton>
                            </mui.Stack>
                        }
                    />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    // Create the edit control modal component
    const editControlModal = (
        <Modal
            open={editModalOpen}
            size={'md'}
            onClose={handleEditModalClose}
            body={
                <div>
                    <mui.Box sx={{ 
                        p: 1,
                        mb: 3
                    }}>
                        <mui.Typography variant="h6" sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            position: 'relative',
                            '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -8,
                                left: 0,
                                width: '40px',
                                height: '3px',
                                backgroundColor: 'primary.main'
                            }
                        }}>
                            Edit Control
                        </mui.Typography>
                    </mui.Box>

                    {editingControl && (
                        <mui.Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Control ID <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                </mui.Typography>
                                <NormalTextField
                                    error={!!editErrors.CONTROL_ID}
                                    name="CONTROL_ID"
                                    placeholder="Enter control identifier"
                                    value={editingControl?.CONTROL_ID || ''}
                                    onChange={handleEditChange}
                                    helperText={editErrors.CONTROL_ID || ' '}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        mt: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            height: '40px',
                                        }
                                    }}
                                />
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Control Name <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                </mui.Typography>
                                <NormalTextField
                                    error={!!editErrors.CONTROL_TITLE}
                                    name="CONTROL_TITLE"
                                    placeholder="Enter descriptive name"
                                    value={editingControl?.CONTROL_TITLE || ''}
                                    onChange={handleEditChange}
                                    helperText={editErrors.CONTROL_TITLE || ' '}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        mt: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            height: '40px',
                                        }
                                    }}
                                />
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Control Category
                                </mui.Typography>
                                <MultipleSelect
                                    isMultiSelect={false}
                                    defaultValue={editControlsCategory ? editControlsCategory : ''}
                                    placeholderText="Select category"
                                    selectOptions={controlsCategoryOptions}
                                    selectedOptions={editControlsCategory ? editControlsCategory : ''}
                                    handleChange={handleEditControlsCategoryChange}
                                    customStyles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                            height: '40px',
                                            fontSize: '0.875rem'
                                        })
                                    }}
                                />
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Control Description <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                </mui.Typography>
                                <mui.Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                    <mui.Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleEditGenerateDescription}
                                        disabled={editDescriptionLoading || !editingControl?.CONTROL_TITLE}
                                        startIcon={editDescriptionLoading ? <CircularProgress size={16} color="inherit" /> : <i className="material-icons" style={{ fontSize: '16px' }}>auto_awesome</i>}
                                        sx={{
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderColor: 'rgba(25, 118, 210, 0.08)',
                                            color: 'primary.main',
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                            '&:hover': {
                                                borderColor: 'rgba(25, 118, 210, 0.12)',
                                                backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                            }
                                        }}
                                    >
                                        {editDescriptionLoading ? 'Generating...' : 'Generate with AI'}
                                    </mui.Button>
                                </mui.Box>
                                <NormalTextField
                                    error={!!editErrors.CONTROL_DESCRIPTION}
                                    name="CONTROL_DESCRIPTION"
                                    placeholder="Enter detailed description of the control"
                                    value={editingControl?.CONTROL_DESCRIPTION || ''}
                                    onChange={handleEditChange}
                                    isMultiLine={true}
                                    rows="4"
                                    helperText={editErrors.CONTROL_DESCRIPTION || ' '}
                                    fullWidth
                                    sx={{
                                        mt: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                        }
                                    }}
                                />
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Control Domain
                                </mui.Typography>
                                <MultipleSelect
                                    isMultiSelect={false}
                                    defaultValue={editControlsDomain ? editControlsDomain : ''}
                                    placeholderText="Select domain"
                                    selectOptions={controlsDomainOptions}
                                    selectedOptions={editControlsDomain ? editControlsDomain : ''}
                                    handleChange={handleEditControlsDomainChange}
                                    customStyles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                            height: '40px',
                                            fontSize: '0.875rem'
                                        })
                                    }}
                                />
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Control Type
                                </mui.Typography>
                                <MultipleSelect
                                    isMultiSelect={false}
                                    defaultValue={editControlsType ? editControlsType : ''}
                                    placeholderText="Select type"
                                    selectOptions={controlsTypeOptions}
                                    selectedOptions={editControlsType ? editControlsType : ''}
                                    handleChange={handleEditControlsTypeChange}
                                    customStyles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                            height: '40px',
                                            fontSize: '0.875rem'
                                        })
                                    }}
                                />
                            </mui.Box>
                        </mui.Box>
                    )}
                </div>
            }
            footer={
                <mui.Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', p: 2 }}>
                    <mui.Button 
                        variant="outlined" 
                        onClick={handleEditModalClose}
                        sx={{
                            py: 1,
                            px: 2,
                            borderRadius: '4px',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: 'rgba(25, 118, 210, 0.08)',
                            color: 'text.primary',
                            '&:hover': {
                                borderColor: 'rgba(25, 118, 210, 0.12)',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                    >
                        Cancel
                    </mui.Button>
                    <mui.Button 
                        variant="contained" 
                        onClick={updateControl}
                        disabled={!editFormValid || editIsLoading}
                        startIcon={editIsLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            py: 1,
                            px: 2,
                            borderRadius: '4px',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            boxShadow: 'none',
                            backgroundColor: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                            },
                            '&:disabled': {
                                backgroundColor: editIsLoading ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                                color: editIsLoading ? 'white' : 'rgba(0, 0, 0, 0.26)',
                                opacity: editIsLoading ? 0.7 : 1
                            }
                        }}
                    >
                        {editIsLoading ? 'Updating...' : 'Update Control'}
                    </mui.Button>
                </mui.Stack>
            }
        />
    );

    return (
        <Suspense fallback="Loading...">
            <div>
                <IASideBar mainContent={customMainContent} />
                {editControlModal}
                <mui.Dialog
                    open={deleteConfirmOpen}
                    onClose={handleDeleteConfirmClose}
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                            maxWidth: '400px',
                            width: '100%'
                        }
                    }}
                >
                    <mui.Box sx={{ pt: 2, px: 2 }}>
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <DeleteIcon sx={{ color: 'error.main', mr: 1.5, fontSize: '22px' }} />
                            <mui.Typography variant="h6" sx={{ 
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                color: 'text.primary'
                            }}>
                                Delete Control
                            </mui.Typography>
                        </mui.Box>
                        <mui.Divider sx={{ mt: 1.5, mb: 2 }} />
                        
                        <mui.Box sx={{ px: 1, pb: 2 }}>
                            <mui.Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                Are you sure you want to delete this control?
                            </mui.Typography>
                            
                            <mui.Box sx={{ 
                                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                p: 1.5,
                                borderRadius: '4px',
                                mt: 2,
                                mb: 1
                            }}>
                                <mui.Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {controlToDelete?.CONTROL_ID || ''}
                                </mui.Typography>
                                <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {controlToDelete?.CONTROL_TITLE || ''}
                                </mui.Typography>
                            </mui.Box>
                            
                            <mui.Typography 
                                variant="body2" 
                                color="error.main" 
                                sx={{ 
                                    mt: 2, 
                                    display: 'flex', 
                                    alignItems: 'center'
                                }}
                            >
                                <WarningAmberRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                This action cannot be undone
                            </mui.Typography>
                            
                            <mui.Box sx={{ 
                                mt: 2, 
                                p: 1.5, 
                                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                borderLeft: '3px solid',
                                borderColor: 'warning.main',
                                borderRadius: '2px'
                            }}>
                                <mui.Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}>
                                    <WarningAmberRoundedIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                                    Note: Controls that are referenced in audits cannot be deleted until all references are removed.
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                    </mui.Box>

                    <mui.DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
                        <mui.Button 
                            onClick={handleDeleteConfirmClose} 
                            variant="outlined"
                            disabled={deleteIsLoading}
                            sx={{
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                color: 'text.primary',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    borderColor: 'rgba(0, 0, 0, 0.2)'
                                }
                            }}
                        >
                            Cancel
                        </mui.Button>
                        <LoadingButton
                            onClick={handleDeleteConfirm}
                            variant="contained"
                            loading={deleteIsLoading}
                            sx={{ 
                                textTransform: 'none',
                                backgroundColor: 'error.main',
                                '&:hover': {
                                    backgroundColor: 'error.dark'
                                },
                                '&.Mui-disabled': { 
                                    color: 'rgba(255, 255, 255, 0.7)', 
                                    backgroundColor: 'error.main',
                                    opacity: 0.7
                                } 
                            }}
                        >
                            {deleteIsLoading ? 'Deleting...' : 'Delete Control'}
                        </LoadingButton>
                    </mui.DialogActions>
                </mui.Dialog>

                {/* Dependency Alert Dialog */}
                {dependencyInfo && (
                    <mui.Dialog
                        open={!!dependencyInfo}
                        onClose={() => setDependencyInfo(null)}
                        PaperProps={{
                            sx: {
                                borderRadius: '8px',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                                maxWidth: '450px',
                                width: '100%'
                            }
                        }}
                    >
                        <mui.Box sx={{ pt: 2, px: 2 }}>
                            <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <WarningAmberRoundedIcon sx={{ color: 'warning.main', mr: 1.5, fontSize: '22px' }} />
                                <mui.Typography variant="h6" sx={{ 
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    color: 'text.primary'
                                }}>
                                    Cannot Delete Control
                                </mui.Typography>
                            </mui.Box>
                            <mui.Divider sx={{ mt: 1.5, mb: 2 }} />
                            
                            <mui.Box sx={{ px: 1, pb: 2 }}>
                                <mui.Typography variant="body1" sx={{ mb: 2 }}>
                                    The control <strong>{dependencyInfo.controlId}</strong> - "{dependencyInfo.controlName}" cannot be deleted because it is referenced in {dependencyInfo.affectedTable}.
                                </mui.Typography>
                                
                                <mui.Box sx={{ 
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    p: 2,
                                    borderRadius: '4px',
                                    mb: 2,
                                    borderLeft: '3px solid',
                                    borderColor: 'primary.main',
                                }}>
                                    <mui.Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                        To delete this control, you need to:
                                    </mui.Typography>
                                    <mui.Box component="ol" sx={{ ml: 2, mt: 0, mb: 0, pl: 0 }}>
                                        <mui.Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                            Find all audit files using this control
                                        </mui.Typography>
                                        <mui.Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                            Remove the references to this control in those audits
                                        </mui.Typography>
                                        <mui.Typography component="li" variant="body2">
                                            Try deleting the control again
                                        </mui.Typography>
                                    </mui.Box>
                                </mui.Box>
                            </mui.Box>
                        </mui.Box>

                        <mui.DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
                            <mui.Button 
                                onClick={() => setDependencyInfo(null)} 
                                variant="contained"
                                color="primary"
                                sx={{
                                    textTransform: 'none',
                                }}
                            >
                                I Understand
                            </mui.Button>
                        </mui.DialogActions>
                    </mui.Dialog>
                )}
            </div>
        </Suspense>
    );



}
export default ControlsLibrary;